/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { initialize } from "../../actions";
import { ACKNOWLEDGE_UPDATE_SAVED_OPEN_HOUSE } from "../../graphql/acknowledgeUpdateSavedOpenHouse";
import { CONFIRM_SAVED_OPEN_HOUSE_ATTENDANCE } from "../../graphql/confirmSavedOpenHouseAttendance";
import { DEREGISTER_FOR_OPEN_HOUSE } from "../../graphql/deregisterForOH";
import { MY_OPEN_HOUSES } from "../../graphql/myOpenHouses";

const initialState = {
  count: null,
  nearestDate: null,
  type: "active",
  listingType: "Sale",
  myRecentOpenHouses: [],
  myUpcomingOpenHouses: [],
  openHousesLoading: false,
  removedOHs: [],
  upcomingNumberOpenHouses: 0,
  recentNumberOpenHouses: 0,
  acknowledgeOHs: [],
  attendedOHs: [],
  initialNumberUOH: null
};

const acknowledgeUpdate = createAsyncThunk(
  "openHouses/acknowledgeUpdate",
  async (props, { extra }) => {
    const { graphqlMutationService } = extra;

    return graphqlMutationService
      .mutate({
        mutation: ACKNOWLEDGE_UPDATE_SAVED_OPEN_HOUSE,
        variables: { saved_open_house_id: props.savedOHId }
      })
      .then(({ data }) => {
        if (data.acknowledge_update_saved_open_house.successful) {
          return props.ohId;
        }
        return null;
      });
  }
);

const confirmAttendance = createAsyncThunk(
  "openHouses/confirmAttendance",
  async (props, { extra }) => {
    const { analyticsService, graphqlMutationService } = extra;

    return graphqlMutationService
      .mutate({
        mutation: CONFIRM_SAVED_OPEN_HOUSE_ATTENDANCE,
        variables: {
          saved_open_house_id: parseInt(props.savedOHId, 10),
          attended: props.attended
        }
      })
      .then(({ data }) => {
        if (data.confirm_saved_open_house_attendance.successful) {
          analyticsService.sendSelfReportAttendedEvent({
            answer: props.attended ? "yes" : "no",
            id: props.ohId
          });
          return { id: props.ohId, attended: props.attended };
        }
        return {};
      });
  }
);

const getMyOpenHouses = createAsyncThunk(
  "openHouses/getMyOpenHouses",
  async (_, { extra, getState }) => {
    const { graphqlQueryService } = extra;
    const state = getState();
    const { listingType } = state.open_houses;

    return graphqlQueryService
      .query({
        query: MY_OPEN_HOUSES,
        variables: { listing_type: listingType.toLowerCase() }
      })
      .then(({ data }) => data);
  }
);

const removeOH = createAsyncThunk(
  "openHouses/removeOH",
  async (openHouseId, { extra }) => {
    const { analyticsService, graphqlMutationService } = extra;

    return graphqlMutationService
      .mutate({
        mutation: DEREGISTER_FOR_OPEN_HOUSE,
        variables: { open_house_id: openHouseId }
      })
      .then(({ data }) => {
        if (data.deregister_for_open_house.successful) {
          analyticsService.sendUnsaveOpenHouseEvent({ openHouseId });
          return openHouseId;
        }
        return null;
      });
  }
);

const openHousesSlice = createSlice({
  name: "openHouses",
  initialState,
  reducers: {
    clearUnnecessaryOH(state) {
      const oldStateMyUpcomingOH = JSON.parse(
        JSON.stringify(state.myUpcomingOpenHouses)
      );

      state.myUpcomingOpenHouses = oldStateMyUpcomingOH
        .map(item => {
          const ohList = item.my_open_house_items.filter(
            oh => !state.removedOHs.includes(oh.open_house.id)
          );
          return ohList.length > 0
            ? {
                date: item.date,
                my_open_house_items: ohList
              }
            : null;
        })
        .filter(Boolean);
      state.removedOHs = [];

      // update attended property of each open house
      state.myRecentOpenHouses.forEach(day =>
        day.my_open_house_items.forEach(oh => {
          const attendedOH = state.attendedOHs.find(
            aoh => aoh.id === oh.open_house.id
          );
          if (attendedOH) {
            oh.attended = attendedOH.attended;
          }
        })
      );
      state.attendedOHs = [];
    },
    clearOHDate(state) {
      state.OHDate = "";
    }
  },
  extraReducers: {
    [initialize]: (state, action) => {
      const {
        count,
        listing_type,
        nearest_date,
        oh_date,
        oh_drawer,
        type
      } = action.payload;

      state.count = count;
      state.nearestDate = nearest_date;
      state.type = type;
      state.listingType = listing_type;
      state.initialNumberUOH = type === "active" ? count : null;
      state.isOHDrawerOpen = oh_drawer === "1";
      state.OHDate = oh_date;
    },
    [getMyOpenHouses.pending]: state => {
      state.openHousesLoading = true;
    },
    [getMyOpenHouses.fulfilled]: (state, action) => {
      state.openHousesLoading = false;
      state.myRecentOpenHouses = action.payload.my_open_houses.recent;
      state.myUpcomingOpenHouses = action.payload.my_open_houses.upcoming;
      state.upcomingNumberOpenHouses = action.payload.my_open_houses.upcoming
        .map(item => item.my_open_house_items.length)
        .reduce((a, b) => a + b, 0);
      state.recentNumberOpenHouses = action.payload.my_open_houses.recent
        .map(item => item.my_open_house_items.length)
        .reduce((a, b) => a + b, 0);
    },
    [removeOH.fulfilled]: (state, action) => {
      if (action.payload) {
        state.removedOHs.push(action.payload);
        if (state.count - 1 === 0 && state.recentNumberOpenHouses > 0) {
          state.count = state.recentNumberOpenHouses;
          state.type = "";
          state.nearestDate =
            state.myRecentOpenHouses[0].my_open_house_items[0].oh_starts_on;
        } else {
          state.count -= 1;
          state.upcomingNumberOpenHouses -= 1;
          if (state.upcomingNumberOpenHouses > 1) {
            state.nearestDate =
              state.myUpcomingOpenHouses[0].my_open_house_items[0].oh_starts_on;
          }
        }
      }
    },
    [acknowledgeUpdate.fulfilled]: (state, action) => {
      state.acknowledgeOHs.push(action.payload);
    },
    [confirmAttendance.fulfilled]: (state, action) => {
      if (action.payload.id) {
        // save { id, attended } in attendedOHs
        state.attendedOHs.push(action.payload);
      }
    }
  }
});

export default openHousesSlice.reducer;
export const { clearOHDate, clearUnnecessaryOH } = openHousesSlice.actions;
export { acknowledgeUpdate, confirmAttendance, getMyOpenHouses, removeOH };
